/**
 * The sign-up screens' rules, pure, so signupRules.test.ts reads them: the
 * band of a rank, its colour and its label, the grid's spots on the map, the
 * button labels, the countdown, prices, offers and currencies, dates, the
 * last step's calendar words, and the step on the address. The owner reads
 * "spots" and "Google's Local Pack" (flow A2, C7); the code keeps its square
 * and pack names.
 *
 * Anand, 01 Nov 26: three bands wherever a rank is shown on the sign-up,
 * green for #1 to 5, orange for #6 to 20, and "20+" in red outside the top
 * 20 or when the rank is unknown. What the server counts (top3, the green of
 * isGreen) is unchanged: only what is drawn moved.
 *
 * Nothing here invents a number: every label is built from the summary, the
 * reads or the billing answer, and a part whose number is missing is left
 * out of the sentence.
 */
import { formatShortDate } from '@/components/gbp-agent/agentTypes';
// B1's four weeks (score.ts planWeeks, pure): the plan screen draws the same titles B3 stores as the locked plan items.
export { planWeeks } from '../../../../../scripts/gbp-agent/signup/score';
import { hasGrid, type FirstHourPayload, type GridPayload, type MarketBusiness, type SignupSummary, type SummaryRow, type TileState, type PlaceSuggestion } from './signupTypes';

/* ------------------------------------------------------------------ bands */

export type Band = 'top5' | 'top20' | 'out';

/** Anand, 01 Nov 26: top5 rank 1 to 5, top20 6 to 20, out when not in the top 20 or unknown (null, not finite, under 1 or over 20). */
export function bandOf(rank: number | null | undefined): Band {
  if (rank == null || !Number.isFinite(rank) || rank < 1 || rank > 20) return 'out';
  return rank <= 5 ? 'top5' : 'top20';
}

/** "Green" in the counts is the top 3 at a spot (contract; it feeds the top3 the server stores), not the band drawn. */
export function isGreen(rank: number | null | undefined): boolean {
  return rank != null && Number.isFinite(rank) && rank >= 1 && rank <= 3;
}

/** The bands' fills, on the map's spots and behind a rank label: green, orange, red (Anand, 01 Nov 26). */
export const BAND_FILL: Record<Band, string> = {
  top5: '#0e8a63',
  top20: '#e7a700',
  out: '#e0566e',
};

/** The rank label's colour on each fill. */
export const BAND_TEXT: Record<Band, string> = {
  top5: '#ffffff',
  top20: '#3b2a00',
  out: '#ffffff',
};

export const BAND_LEGEND: Array<{ band: Band; words: string }> = [
  { band: 'top5', words: '#1 to 5' },
  { band: 'top20', words: '#6 to 20' },
  { band: 'out', words: '20+' },
];

/** A rank as the owner reads it: "#6", or "20+" outside the top 20 or when it is unknown (Anand, 01 Nov 26). */
export function rankLabel(rank: number | null | undefined): string {
  return bandOf(rank) === 'out' ? '20+' : `#${rank}`;
}

/* ------------------------------------------------------------------- grid */

/** scripts/gbp-agent/own-grid-scan.ts KM_PER_DEG_LAT, so the spots sit where the scan read them. */
const KM_PER_DEG_LAT = 111.32;
export const GRID_SIZE = 5;

/** gridPoints(lat, lng, 5, spacing) of own-grid-scan.ts: north to south, west to east, index 0 north-west. */
export function squareCentres(lat: number, lng: number, size = GRID_SIZE, spacingKm = 0.25): Array<{ lat: number; lng: number }> {
  const half = (size - 1) / 2;
  const dLat = spacingKm / KM_PER_DEG_LAT;
  const dLng = spacingKm / (KM_PER_DEG_LAT * Math.cos((lat * Math.PI) / 180));
  const out: Array<{ lat: number; lng: number }> = [];
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) out.push({ lat: lat + (half - r) * dLat, lng: lng + (c - half) * dLng });
  }
  return out;
}

/** One spot's edges, the spacing wide, around its centre. */
export function squareBounds(centre: { lat: number; lng: number }, spacingKm = 0.25): { north: number; south: number; east: number; west: number } {
  const halfLat = spacingKm / 2 / KM_PER_DEG_LAT;
  const halfLng = spacingKm / 2 / (KM_PER_DEG_LAT * Math.cos((centre.lat * Math.PI) / 180));
  return { north: centre.lat + halfLat, south: centre.lat - halfLat, east: centre.lng + halfLng, west: centre.lng - halfLng };
}

/** Every market business's rank at every spot comes from the same grid read (contract). */
export function cellsFor(payload: GridPayload | null | undefined, placeId: string | null | undefined, size = GRID_SIZE): Array<number | null> {
  const out: Array<number | null> = Array.from({ length: size * size }, () => null);
  if (!payload || !placeId) return out;
  for (const sq of payload.squares ?? []) {
    if (sq.n < 0 || sq.n >= out.length || sq.no_data) continue;
    const hit = (sq.listings ?? []).find((l) => l.place_id === placeId);
    out[sq.n] = hit ? hit.rank : null;
  }
  return out;
}

export function top3Count(cells: Array<number | null>): number {
  return cells.filter((c) => isGreen(c)).length;
}

/** The centre spot is "at your door" (index 12 of 25). */
export function doorRank(cells: Array<number | null> | null | undefined): number | null {
  if (!cells || cells.length < GRID_SIZE * GRID_SIZE) return null;
  return cells[Math.floor((GRID_SIZE * GRID_SIZE) / 2)] ?? null;
}

/* ------------------------------------------------------------------ words */

/** "dentist in rahatani" -> "Rahatani": the primary search is "<category word> in <locality>" (contract). */
export function localityOf(primary: string | null | undefined): string | null {
  const m = /\bin\s+(.+)$/i.exec(String(primary ?? '').trim());
  if (!m) return null;
  return m[1]
    .split(/\s+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');
}

export function keywordOf(summary: Pick<SignupSummary, 'keywords'>, role: 'primary' | 'money' | 'category'): string | null {
  return summary.keywords.find((k) => k.role === role)?.keyword ?? null;
}

const n0 = (n: number) => n.toLocaleString('en-IN');

/** "40 searches a month in Pune", or null when no volume was read. */
export function volumeLine(volume: number | null | undefined, market: string | null | undefined): string | null {
  if (volume == null || !Number.isFinite(volume)) return null;
  const where = market ? ` in ${market}` : '';
  if (volume < 10) return `under 10 searches a month${where}`;
  return `${n0(volume)} searches a month${where}`;
}

/** The owner's replies still waiting once the first hour's replies went out; null when the count is not on hand. */
export function remainingReplies(summary: Pick<SignupSummary, 'self_waiting'> | null | undefined, firstHour: FirstHourPayload | null | undefined): number | null {
  const waiting = summary?.self_waiting;
  if (waiting == null || !Number.isFinite(waiting)) return null;
  const sent = (firstHour?.replies ?? []).filter((r) => !!r.sent_at).length;
  return Math.max(0, waiting - sent);
}

/** How long a business name may run on the cards and in the table (the mock's "Dr. Aarohi's", "DentoSquare"). */
export const SHORT_NAME_CHARS = 22;
const TRAILING_JOINERS = /\s+(?:&|and|in|of|the|at|by|for|[-,|:])$/i;

/**
 * A full Google title shortened at a word boundary for display, the full name
 * kept for the title attribute: "Galaxy Dental Clinic Pimple Saudagar Pune" ->
 * "Galaxy Dental Clinic". A name that fits is left as it is.
 */
export function shortName(name: string, max = SHORT_NAME_CHARS): string {
  const full = name.trim().replace(/\s+/g, ' ');
  if (full.length <= max) return full;
  let cut = full.slice(0, max + 1);
  const space = cut.lastIndexOf(' ');
  cut = space > 0 ? cut.slice(0, space) : full.slice(0, max);
  let prev = '';
  while (prev !== cut) {
    prev = cut;
    cut = cut.replace(TRAILING_JOINERS, '').replace(/[\s,&\-|:]+$/, '');
  }
  return cut || full.slice(0, max);
}

/** The owner's own column of the summary. */
export function selfColumn(summary: Pick<SignupSummary, 'columns'>) {
  return summary.columns.find((c) => c.is_self) ?? null;
}

/** "Galaxy Dental is #1 at 6 of the 7 spots where you are not in the top 3.", from gridStats.holders. */
export function holderLine(summary: Pick<SignupSummary, 'grids'>, keyword: string): string | null {
  const g = summary.grids[keyword]?.self;
  const top = g?.holders?.length ? [...g.holders].sort((a, b) => b.count - a.count)[0] : null;
  if (!g || !top) return null;
  const missing = GRID_SIZE * GRID_SIZE - g.top3;
  return `${shortName(top.title)} is #1 at ${top.count} of the ${missing} ${missing === 1 ? 'spot' : 'spots'} where you are not in the top 3.`;
}

/** The searches with a grid, in the assessment's order (primary, then money by money_rank): flow A2, C1. */
export function gridKeywords(summary: Pick<SignupSummary, 'keywords'>): string[] {
  const out: string[] = [];
  for (const k of summary.keywords) if (hasGrid(k) && k.keyword && !out.includes(k.keyword)) out.push(k.keyword);
  return out;
}

/* ------------------------------------------------------------ table rows */

export type RowGroup = 'pack' | 'rank' | 'reviews' | 'profile';

/** The table's group rows, by the row keys of the contract. */
export function groupOf(key: string): RowGroup {
  if (key === 'pack') return 'pack';
  if (key.startsWith('rank:') || key.startsWith('squares:')) return 'rank';
  if (key === 'sov' || key === 'reviews' || key === 'answered' || key === 'reply_quality') return 'reviews';
  return 'profile';
}

/** The group row over each run of facets. */
export function groupLabel(group: RowGroup, summary: SignupSummary): { title: string; sub: string | null } {
  if (group === 'pack') return { title: "Google's top 3", sub: "Google's Local Pack: the 3 businesses Google shows first, with the map" };
  if (group === 'rank') return { title: 'Your rank on Google Maps at your address', sub: 'from the middle spot of the maps above' };
  if (group === 'reviews') return { title: 'Reviews', sub: null };
  return { title: 'Profile', sub: null };
}

/**
 * A table row's label and the line under it in plain words (Anand, 01 Nov 26:
 * "use layman's terms"), by the row keys of the contract; any other row keeps
 * the server's words. Pure.
 */
export function plainRow(row: Pick<SummaryRow, 'key' | 'label' | 'sub'>, summary: Pick<SignupSummary, 'keywords' | 'market'>): { label: string; sub: string | null } {
  const k = row.key;
  const primary = keywordOf(summary, 'primary');
  const m = summary.market;
  if (k === 'pack') return { label: "In Google's top 3", sub: primary ? `"${primary}", searched on a phone near you` : null };
  if (k.startsWith('squares:')) return { label: `Spots where you are in the top 3: "${k.slice(8)}"`, sub: 'out of 25 spots around you' };
  if (k === 'sov') return { label: 'Your share of reviews nearby', sub: m?.count ? `of the ${n0(m.total_reviews)} reviews of the ${m.count} businesses near you` : null };
  if (k === 'answered') return { label: 'Reviews replied to', sub: 'of the latest 100' };
  if (k === 'reply_quality') return { label: 'How good the replies are', sub: 'out of 100: names the service and the area, not copy and paste' };
  if (k === 'posts') return { label: 'Posts, last 30 days', sub: null };
  if (k === 'photos') {
    const median = /^market median (\d+)$/.exec(row.sub ?? '');
    return { label: 'Photos on Google', sub: median ? `a typical business nearby has ${median[1]}` : row.sub };
  }
  if (k === 'busy') return { label: 'Busy hours', sub: 'as Google shows them' };
  if (k === 'profile_score') return { label: 'Google profile score', sub: 'out of 100' };
  if (k === 'website_score') return { label: 'Website score', sub: 'out of 100' };
  return { label: row.label, sub: row.sub };
}

/**
 * The scorecard's headline (score.ts headline, the server's words) in the
 * sign-up's plain words: "at your door" reads "at your address", the Local
 * Pack reads "Google's top 3". Pure.
 */
export function plainHeadline(headline: string): string {
  return headline
    .replace(/ at your door\b/g, ' at your address')
    .replace(/missing from Google's Local Pack/g, "not in Google's top 3")
    .replace(/in Google's Local Pack/g, "in Google's top 3");
}

/** A cell's small line in plain words: "+3 in 30 d" reads "+3 in 30 days", "median 2 days" reads "replies in about 2 days". Pure. */
export function plainSmall(small: string): string {
  return small
    .replace(/ in 30 d\b/g, ' in 30 days')
    .replace(/\bmedian (\d+) (days?)\b/g, 'replies in about $1 $2')
    .replace(/\bsame day\b/g, 'replies the same day')
    .replace(/, in the Local Pack:/g, ", in Google's top 3:");
}

/** Which read a cell waits on, so a running read shimmers and a failed one says so. */
export function readKeyFor(rowKey: string, placeId: string): string {
  if (rowKey === 'pack') return 'pack';
  if (rowKey.startsWith('rank:')) return `grid:${rowKey.slice(5)}`;
  if (rowKey.startsWith('squares:')) return `grid:${rowKey.slice(8)}`;
  if (rowKey === 'sov' || rowKey === 'photos') return 'market';
  if (rowKey === 'reviews' || rowKey === 'answered' || rowKey === 'reply_quality') return `reviews:${placeId}`;
  if (rowKey === 'posts' || rowKey === 'busy') return 'posts_busy';
  if (rowKey === 'profile_score') return `audit:${placeId}`;
  if (rowKey === 'website_score') return `website:${placeId}`;
  return rowKey;
}

/** A cell with no value yet: shimmer while its read runs, "could not read" when it failed. */
export function cellLook(value: unknown, readStatus: string | null | undefined, assessmentRunning: boolean): 'value' | 'shimmer' | 'failed' | 'empty' {
  if (value !== null && value !== undefined && value !== '') return 'value';
  if (readStatus === 'failed') return 'failed';
  if (readStatus === 'running' || (readStatus == null && assessmentRunning)) return 'shimmer';
  return 'empty';
}

/** A number in a cell, for the phone's bars; null for words such as "none". */
export function cellNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const m = /^#?(-?\d+(?:\.\d+)?)%?$/.exec(value.trim().replace(/,/g, ''));
    return m ? Number(m[1]) : null;
  }
  return null;
}

/* ------------------------------------------------------------- the market */

/** The five, most reviewed first; the rest of the market, the Local Pack first. */
export function marketSets(rows: MarketBusiness[]) {
  const byReviews = (a: MarketBusiness, b: MarketBusiness) => (b.reviews_count ?? 0) - (a.reviews_count ?? 0);
  // The five sit in their seats (pick_rank, the seat a swapped-in business takes from the one it replaces); rows with no seat follow by reviews.
  const bySeat = (a: MarketBusiness, b: MarketBusiness) => (a.pick_rank ?? 99) - (b.pick_rank ?? 99) || byReviews(a, b) || a.name.localeCompare(b.name);
  const five = rows.filter((b) => b.is_owner_pick && !b.is_self).sort(bySeat);
  const rest = rows.filter((b) => !b.is_owner_pick && !b.is_self).sort((a, b) => Number(b.pack_rank != null) - Number(a.pack_rank != null) || byReviews(a, b));
  const packOutside = rest.filter((b) => b.pack_rank != null).sort((a, b) => (a.pack_rank ?? 9) - (b.pack_rank ?? 9));
  return { five, rest, packOutside, self: rows.find((b) => b.is_self) ?? null };
}

/**
 * The market with one of the five swapped for another: `into` takes the seat
 * of `out` and every seat is written (pick_rank 1..5 in the order shown), so
 * the five never reorder on a swap (Anand, 31 Oct 26). A business not in the
 * market yet (typed on the page) comes in as `added`. Pure.
 */
export function swapped(rows: MarketBusiness[], out: string, into: string, added?: MarketBusiness): MarketBusiness[] {
  const seats = marketSets(rows).five.map((b) => b.place_id);
  const at = seats.indexOf(out);
  if (at < 0 || out === into) return rows;
  seats[at] = into;
  const seat = new Map(seats.map((id, i) => [id, i + 1]));
  const base = rows.some((b) => b.place_id === into) || !added ? rows : [...rows, added];
  return base.map((b) => (b.is_self ? b : seat.has(b.place_id) ? { ...b, is_owner_pick: true, pick_rank: seat.get(b.place_id)! } : b.place_id === out ? { ...b, is_owner_pick: false, pick_rank: null } : b));
}

/** A typed business before Google's facts land: the name and the place id, nothing made up. Pure. */
export function placeholderRow(s: PlaceSuggestion): MarketBusiness {
  return { place_id: s.place_id, cid: null, name: s.name, category: null, lat: null, lng: null, distance_km: null, photo_url: null, website: null, rating: null, reviews_count: null, photos_count: null, is_self: false, is_owner_pick: false, pack_rank: null, pack_keyword: null, pick_rank: null };
}

/* -------------------------------------------------------------- freshness */

export const TILE_WORDS: Record<TileState, string> = { ok: 'Up to date', warn: 'Needs work', bad: 'Out of date', na: 'Could not check' };

/** A key's own words ("holiday_hours" -> "Holiday hours"), for a field B3 names that the fix words do not. */
export function wordsOf(key: string): string {
  const words = key.replace(/[_:]+/g, ' ').trim();
  return words ? words[0].toUpperCase() + words.slice(1) : key;
}

/* ------------------------------------------------------------ the first hour */

const FIX_WORDS: Record<string, string> = {
  description: 'Description rewritten',
  categories: 'Categories checked',
  additional_categories: 'Categories checked',
  services: 'Services added',
  attributes: 'Attributes set',
  hours: 'Opening hours checked',
  website: 'Website link fixed',
  phone: 'Phone number checked',
};

/** One fix's line: "6 services added: root canal, dentures" when the new value is a list. */
export function fixLabel(field: string, to: unknown): string {
  const base = FIX_WORDS[field] ?? `${wordsOf(field)} updated`;
  if (Array.isArray(to) && to.length && to.every((t) => typeof t === 'string')) {
    const list = (to as string[]).slice(0, 6).join(', ');
    if (field === 'services') return `${to.length} ${to.length === 1 ? 'service' : 'services'} added: ${list}`;
    if (field === 'categories' || field === 'additional_categories') return `Categories checked: ${list}`;
  }
  return base;
}

/** The location's reviews on Google, by the assessment's pin.place_id (batch 2 contract); null without a place id. */
export function googleReviewsUrl(placeId: string | null | undefined): string | null {
  const id = typeof placeId === 'string' ? placeId.trim() : '';
  return id ? `https://search.google.com/local/reviews?placeid=${encodeURIComponent(id)}` : null;
}

/* ------------------------------------------------------------------ dates */

/** hh:mm, 24 hour, in the browser's zone. */
export function hhmm(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** dd Mon yy (house rule). */
export function ddMonYy(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : formatShortDate(d);
}

/** "Offer ends 02 Nov 26, 23:59". */
export function offerEndsText(endsAt: string): string {
  return `Offer ends ${ddMonYy(endsAt)}, ${hhmm(endsAt)}`;
}

/** The months an offer covers once taken (signup-v2.ts cycles: 3), or null when it carries none. */
export function offerCycles(offer: { cycles?: number | null } | null | undefined): number | null {
  const c = offer?.cycles;
  return typeof c === 'number' && Number.isFinite(c) && c >= 1 ? Math.floor(c) : null;
}

const monthsWords = (n: number) => `${n} ${n === 1 ? 'month' : 'months'}`;

/** The offer's badge: "40% off for 3 months" for an offer that covers months (Anand, 01 Nov 26), else "30% off". */
export function offerBadge(offer: { percent: number; cycles?: number | null }): string {
  const c = offerCycles(offer);
  return c ? `${offer.percent}% off for ${monthsWords(c)}` : `${offer.percent}% off`;
}

/** "Upgrade now to keep it, until 01 Nov 26, 23:59": the end of an offer that covers months. */
export function offerKeepText(endsAt: string): string {
  return `Upgrade now to keep it, until ${ddMonYy(endsAt)}, ${hhmm(endsAt)}`;
}

/* ------------------------------------------------------------ the calendar */

const DAY_MS = 86_400_000;
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** A yyyy-mm-dd day at UTC noon and read with UTC getters, so no zone moves it; null when it does not parse. */
function calendarDate(day: string | null | undefined): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(day ?? '').slice(0, 10));
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12));
  return Number.isNaN(d.getTime()) ? null : d;
}
const parts = (d: Date) => ({ dd: String(d.getUTCDate()).padStart(2, '0'), mon: MONTHS[d.getUTCMonth()], yy: String(d.getUTCFullYear() % 100).padStart(2, '0'), wd: WEEKDAYS[d.getUTCDay()] });

/** A post's date on the last step's post calendar, "Mon 03 Nov 26" (dd Mon yy with the weekday); the day as given when it does not parse. */
export function calendarDayLong(day: string): string {
  const d = calendarDate(day);
  if (!d) return day;
  const p = parts(d);
  return `${p.wd} ${p.dd} ${p.mon} ${p.yy}`;
}

/** A calendar row's day, "Mon 03"; the day as given when it does not parse. */
export function calendarDay(day: string): string {
  const d = calendarDate(day);
  if (!d) return day;
  const p = parts(d);
  return `${p.wd} ${p.dd}`;
}

/**
 * A calendar week's header in dd Mon yy parts: "Today, Sun 01 Nov 26" for
 * week 0; "Week 1, 02 to 08 Nov 26", "Week 4, 23 to 29 Nov 26", "Week 1,
 * 28 Sep to 04 Oct 26" or "Week 1, 29 Dec 25 to 04 Jan 26" after it (a week
 * runs from its Monday, starts_on, to the Sunday).
 */
export function calendarWeekTitle(week: { week: number; starts_on: string }): string {
  const d = calendarDate(week.starts_on);
  if (!d) return week.week === 0 ? 'Today' : `Week ${week.week}`;
  const a = parts(d);
  if (week.week === 0) return `Today, ${a.wd} ${a.dd} ${a.mon} ${a.yy}`;
  const b = parts(new Date(d.getTime() + 6 * DAY_MS));
  const range = a.yy !== b.yy ? `${a.dd} ${a.mon} ${a.yy} to ${b.dd} ${b.mon} ${b.yy}` : a.mon !== b.mon ? `${a.dd} ${a.mon} to ${b.dd} ${b.mon} ${b.yy}` : `${a.dd} to ${b.dd} ${b.mon} ${b.yy}`;
  return `Week ${week.week}, ${range}`;
}

/**
 * The first post's topic for the trial box: the calendar's week 0 post (C2:
 * the money_rank 1 keyword, else the primary), else the money_rank 1
 * keyword, else the first money keyword, else the primary; null when the
 * summary names none.
 */
export function firstPostTopic(summary: Pick<SignupSummary, 'keywords' | 'calendar'> | null | undefined): string | null {
  if (!summary) return null;
  const today = summary.calendar?.weeks?.find((w) => w.week === 0)?.items.find((i) => i.kind === 'post' && !!i.topic?.trim());
  if (today?.topic) return today.topic.trim();
  const money = summary.keywords.filter((k) => k.role === 'money');
  return (money.find((k) => k.money_rank === 1) ?? money[0])?.keyword ?? keywordOf(summary, 'primary');
}

/** "all four searches", "both searches", "your search": how many the plan's Monday check reads; null with none. */
export function searchesWords(n: number): string | null {
  if (!Number.isFinite(n) || n < 1) return null;
  if (n === 1) return 'your search';
  if (n === 2) return 'both searches';
  const words = ['', 'one', 'two', 'three', 'four', 'five', 'six'];
  return `all ${words[n] ?? n} searches`;
}

export interface Countdown {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  over: boolean;
}

/** The time left on the offer, ticking on the client. */
export function countdownParts(endsAt: string, now: number): Countdown {
  const left = Math.max(0, new Date(endsAt).getTime() - now);
  const s = Math.floor(left / 1000);
  return { days: Math.floor(s / 86_400), hours: Math.floor((s % 86_400) / 3600), minutes: Math.floor((s % 3600) / 60), seconds: s % 60, over: left <= 0 };
}

/** "2 days 09 h 41 m left on the offer", for a screen reader and the tests. */
export function countdownText(c: Countdown): string {
  if (c.over) return 'The offer has ended';
  const pad = (n: number) => String(n).padStart(2, '0');
  const day = `${c.days} ${c.days === 1 ? 'day' : 'days'}`;
  return `${day} ${pad(c.hours)} h ${pad(c.minutes)} m left on the offer`;
}

/* ------------------------------------------------------------------ money */

/** "Rs 2,499" or "US $24.99", from minor units. Rupees are shown whole. */
export function formatMoney(minor: number, currency: string): string {
  const cur = currency.toUpperCase();
  const amount = minor / 100;
  if (cur === 'INR') return `Rs ${Math.round(amount).toLocaleString('en-IN')}`;
  if (cur === 'USD') return `US $${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return `${cur} ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** The offer's price: whole rupees, cents for dollars. */
export function offerMinor(listMinor: number, percent: number, currency: string): number {
  const raw = listMinor * (1 - percent / 100);
  return currency.toUpperCase() === 'INR' ? Math.round(raw / 100) * 100 : Math.round(raw);
}

/**
 * Under the price: INR carries the GST line, USD no tax line (S8 USD block).
 * app_billing_get answers gst.rate as a whole-number percent (18, GST_RATE in
 * _shared/gbp-agent-billing.ts), shown as it comes (batch 2, D5).
 */
export function perLine(currency: string, gst: { mode: string; rate: number } | null | undefined): string {
  if (currency.toUpperCase() !== 'INR') return 'a month';
  const rate = gst && gst.mode === 'exclusive' && Number.isFinite(gst.rate) && gst.rate > 0 ? Math.round(gst.rate * 100) / 100 : null;
  return rate ? `+ ${rate}% GST a month` : 'a month';
}

/**
 * Under the offer price of an offer that covers months (Anand, 01 Nov 26):
 * "+ 18% GST a month for 3 months, then Rs 2,499" for INR, "a month for 3
 * months, then US $24.99" for USD.
 */
export function offerPerLine(currency: string, gst: { mode: string; rate: number } | null | undefined, cycles: number, listMinor: number): string {
  return `${perLine(currency, gst)} for ${monthsWords(cycles)}, then ${formatMoney(listMinor, currency)}`;
}

/* ------------------------------------------------------------ the address */

export const SIGNUP_STEPS = ['market', 'business', 'sizing', 'scorecard', 'plan', 'together'] as const;
export type SignupStep = (typeof SIGNUP_STEPS)[number];

/** ?step=<one of SIGNUP_STEPS>, or ?ask=1 for the ask (the home's locked cards link there). */
export function readSignupStep(search: string): SignupStep | null {
  const q = new URLSearchParams(search);
  if (q.get('ask') === '1') return 'together';
  const step = q.get('step');
  return (SIGNUP_STEPS as readonly string[]).includes(step ?? '') ? (step as SignupStep) : null;
}

/** The start page's address for a step (none: the pick): the step and the location on it, every other parameter kept. */
export function withSignupStep(pathWithSearch: string, step: SignupStep | null, locationId: string | null): string {
  const [path, query = ''] = pathWithSearch.split('?');
  const q = new URLSearchParams(query);
  q.delete('ask');
  if (step) q.set('step', step);
  else q.delete('step');
  if (locationId) q.set('location', locationId);
  const s = q.toString();
  return s ? `${path}?${s}` : path;
}

/* ------------------------------------------------------------ the proof */

/** "Joined Fly this week" while four or more joined in 7 days; the route widens to 30 days below that. */
export function proofTitle(proof: { joined_7d: number; joined_30d: number }): { title: string; count: number } {
  if (proof.joined_7d >= 4) return { title: 'Joined Fly this week', count: proof.joined_7d };
  return { title: 'Joined Fly this month', count: proof.joined_30d };
}

/** A website's host, short ("jsmotors.in", no "www."), for the website score parts (Anand, 02 Nov 26); null without one. */
export function siteHost(website: string | null | undefined): string | null {
  const raw = String(website ?? '').trim();
  if (!raw) return null;
  try {
    const host = new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`).hostname.toLowerCase().replace(/^www\./, '');
    return host.includes('.') ? host : null;
  } catch {
    return null;
  }
}

/**
 * A business's website icon: Google's favicon service for the site's host at 128 px (usually the logo mark), or null
 * without a website, when the Logo component shows the initial. The market read's main_image is a storefront or
 * Street View shot, not an identity (Anand, 31 Oct 26: "let's use an icon if it's available").
 * Twin of scripts/gbp-agent/signup/icon.ts siteIcon: keep the two byte-identical.
 */
export function siteIcon(website: string | null | undefined): string | null {
  const raw = String(website ?? '').trim();
  if (!raw) return null;
  try {
    const host = new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`).hostname.toLowerCase();
    if (!host.includes('.')) return null;
    return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=128`;
  } catch {
    return null;
  }
}
